import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Style.css';
import ApiSender from '../../common/ApiLogic';
import useLanguage from '../../common/useLanguage';
import { FALLBACK_PLANS } from '../../common/constants';
import Header from './components/Header';
import SiteFooter from './components/Footer';

// KS_Bundle-style entries (carrier tariff-plan inclusion) come back with no validityDays and
// are not something a subscriber picks for themselves on this page - only real, purchasable
// plans are shown here.
function isPurchasable(plan) {
  return plan.validityDays != null && plan.renewalEveryDays > 0;
}

function describeCycleTitle(renewalEveryDays, language) {
  const isRussian = language === 'RU';
  const isKyrgyz = language === 'KY';
  if (renewalEveryDays === 1) return isRussian ? 'Ежедневно' : isKyrgyz ? 'Күн сайын' : 'Daily';
  if (renewalEveryDays === 30) return isRussian ? 'Ежемесячно' : isKyrgyz ? 'Айына бир жолу' : 'Monthly';
  if (renewalEveryDays === 365) return isRussian ? 'Ежегодно' : isKyrgyz ? 'Жылына бир жолу' : 'Yearly';
  return isRussian ? `Каждые ${renewalEveryDays} дн.` : isKyrgyz ? `Ар ${renewalEveryDays} күндө` : `Every ${renewalEveryDays} days`;
}

function describeCycleSuffix(renewalEveryDays, language) {
  const isRussian = language === 'RU';
  const isKyrgyz = language === 'KY';
  if (renewalEveryDays === 1) return isRussian ? 'в день' : isKyrgyz ? 'күнүнө' : 'per day';
  if (renewalEveryDays === 30) return isRussian ? 'в месяц' : isKyrgyz ? 'айына' : 'per month';
  if (renewalEveryDays === 365) return isRussian ? 'в год' : isKyrgyz ? 'жылына' : 'per year';
  return isRussian ? `каждые ${renewalEveryDays} дн.` : isKyrgyz ? `ар ${renewalEveryDays} күндө` : `every ${renewalEveryDays} days`;
}

function formatPrice(price) {
  return Number.isInteger(price) ? price : Number(price).toFixed(2);
}

const FEATURES = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 22s7-6.03 7-12a7 7 0 1 0-14 0c0 5.97 7 12 7 12Z" stroke="#23c11d" strokeWidth="1.8" strokeLinejoin="round" />
        <circle cx="12" cy="10" r="2.6" stroke="#23c11d" strokeWidth="1.8" />
      </svg>
    ),
    titleRu: 'GPS-геолокация',
    titleKy: 'GPS-геолокация',
    titleEn: 'GPS location',
    descRu: 'Всегда знайте, где находится ребёнок, и получайте уведомление, если он покидает безопасную зону.',
    descKy: 'Балаңыздын кайда жүргөнүн ар дайым билип туруңуз жана ал коопсуз аймактан чыкканда билдирүү алыңыз.',
    descEn: 'Always know where your child is, and get notified if they leave a safe zone.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9" stroke="#23c11d" strokeWidth="1.8" />
        <path d="M12 7v5.2l3.2 2" stroke="#23c11d" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    titleRu: 'Контроль экранного времени',
    titleKy: 'Экран убактысын көзөмөлдөө',
    titleEn: 'Screen time control',
    descRu: 'Устанавливайте лимиты на игры и приложения, чтобы у ребёнка формировались здоровые привычки.',
    descKy: 'Балада ден соолукка пайдалуу адаттар калыптансын үчүн оюндарга жана колдонмолорго чектөө коюңуз.',
    descEn: 'Set limits on games and apps to help your child build healthy habits.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 3 4 6.5V11c0 5 3.4 8.9 8 10 4.6-1.1 8-5 8-10V6.5L12 3Z" stroke="#23c11d" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="m9 12 2 2 4-4" stroke="#23c11d" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    titleRu: 'Фильтрация контента',
    titleKy: 'Мазмунду чыпкалоо',
    titleEn: 'Content filtering',
    descRu: 'Блокируйте нежелательные сайты и приложения, чтобы онлайн-среда ребёнка оставалась безопасной.',
    descKy: 'Балаңыздын онлайн чөйрөсү коопсуз болушу үчүн каалабаган сайттарды жана колдонмолорду бөгөттөңүз.',
    descEn: 'Block unwanted websites and apps to keep your child’s online environment safe.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 3a5 5 0 0 0-5 5v3.1c0 .5-.2 1-.6 1.4L5 14v2h14v-2l-1.4-1.5a2 2 0 0 1-.6-1.4V8a5 5 0 0 0-5-5Z" stroke="#23c11d" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M9.5 19a2.5 2.5 0 0 0 5 0" stroke="#23c11d" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
    titleRu: 'Уведомления родителю',
    titleKy: 'Ата-энеге билдирүүлөр',
    titleEn: 'Alerts for parents',
    descRu: 'Получайте push-уведомления о важных событиях: разряженный телефон, новое устройство и другое.',
    descKy: 'Маанилүү окуялар тууралуу push-билдирүүлөрдү алыңыз: батарея отуруп калганда, жаңы түзмөк кошулганда ж.б.',
    descEn: 'Get push notifications for important events: low battery, a new device, and more.',
  },
];

function PinPage() {
  const nav = useNavigate();
  const location = useLocation();
  const otpBoxRef = useRef(null);
  const formSectionRef = useRef(null);
  const apiSender = useMemo(() => new ApiSender(), []);
  const { language, selectLanguage, t } = useLanguage(location, apiSender);

  const [msisdn, setMsisdn] = useState('');
  const [isMsisdnFocused, setIsMsisdnFocused] = useState(false);
  const [otp, setOtp] = useState('');
  const [warning, setWarning] = useState('');
  const [firstStep, setFirstStep] = useState(true);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [selectedPlanId, setSelectedPlanId] = useState(FALLBACK_PLANS[0].planId);

  useEffect(() => {
    const loadPlans = async () => {
      const response = await apiSender.getPlans('RU');
      if (!apiSender.isSuccess(response)) return;

      const purchasable = (Array.isArray(response.result?.data) ? response.result.data : [])
        .filter(isPurchasable)
        .sort((a, b) => a.renewalEveryDays - b.renewalEveryDays);

      if (purchasable.length === 0) return;

      setPlans(purchasable);
      const dailyPlan = purchasable.find((plan) => plan.renewalEveryDays === 1) || purchasable[0];
      setSelectedPlanId(dailyPlan.planId);
    };

    // Pricing is not language-dependent - fetched once on mount only.
    loadPlans();
  }, [apiSender]);

  useEffect(() => {
    if (!firstStep) {
      otpBoxRef.current?.focus();
    }
  }, [firstStep]);

  useEffect(() => {
    if (location.hash === '#subscribe') {
      formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  const selectedPlan = useMemo(
    () => plans.find((plan) => plan.planId === selectedPlanId) || plans[0] || FALLBACK_PLANS[0],
    [plans, selectedPlanId]
  );

  const bestValuePlanId = useMemo(() => {
    if (plans.length < 2) return null;
    const cheapestPerDay = plans.reduce((best, plan) => {
      const perDay = plan.price / plan.renewalEveryDays;
      return perDay < best.perDay ? { planId: plan.planId, perDay } : best;
    }, { planId: plans[0].planId, perDay: plans[0].price / plans[0].renewalEveryDays });
    return cheapestPerDay.planId;
  }, [plans]);

  const scrollToForm = () => {
    formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleMsisdnChange = (event) => {
    setMsisdn(event.target.value.replace(/\D/g, '').slice(0, 9));
    setWarning('');
  };

  const handleMsisdnFocus = () => setIsMsisdnFocused(true);
  const handleMsisdnBlur = () => setIsMsisdnFocused(false);

  const handleOtpChange = (event) => {
    setOtp(event.target.value.replace(/\D/g, '').slice(0, 4));
    setWarning('');
  };

  const isMsisdnValid = useCallback(() => /^(55|99)\d{7}$/.test(msisdn), [msisdn]);
  const isOtpValid = useCallback(() => /^\d{4}$/.test(otp), [otp]);

  const handleSendOtpClick = async () => {
    if (!isMsisdnValid()) {
      setWarning(t('Неверный формат номера', 'Номердин форматы туура эмес', 'Invalid phone number format'));
      return;
    }

    if (loading) return;

    setLoading(true);
    setWarning('');

    try {
      const response = await apiSender.sendPin({
        msisdn: `996${msisdn}`,
        planId: selectedPlanId,
        language,
        location,
      });

      if (apiSender.isSuccess(response)) {
        setFirstStep(false);
        return;
      }

      setWarning(
        response.result?.message ||
        t('Не удалось отправить код, попробуйте позже', 'Кодду жиберүү мүмкүн болгон жок, кийинчерээк аракет кылыңыз', 'Could not send the code, please try again later')
      );
    } catch (error) {
      console.error('Send OTP failed:', error);
      setWarning(t('Произошла ошибка, попробуйте позже', 'Ката кетти, кийинчерээк аракет кылыңыз', 'Something went wrong, please try again later'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtpClick = async () => {
    if (!isOtpValid()) {
      setWarning(t('Неверный код', 'Код туура эмес', 'Invalid code'));
      return;
    }

    if (loading) return;

    setLoading(true);
    setWarning('');

    try {
      const response = await apiSender.verifyPin({
        msisdn: `996${msisdn}`,
        otp,
        planId: selectedPlanId,
        language,
        location,
      });

      if (apiSender.isSuccess(response)) {
        nav(`/success?lang=${language}`);
        return;
      }

      setWarning(
        response.result?.message ||
        t('Не удалось подтвердить подписку', 'Жазылууну ырастоо мүмкүн болгон жок', 'Could not confirm the subscription')
      );
      setOtp('');
    } catch (error) {
      console.error('Verify OTP failed:', error);
      setWarning(t('Произошла ошибка, попробуйте позже', 'Ката кетти, кийинчерээк аракет кылыңыз', 'Something went wrong, please try again later'));
    } finally {
      setLoading(false);
    }
  };

  const selectedPrice = formatPrice(selectedPlan.price);
  const selectedCycleSuffix = describeCycleSuffix(selectedPlan.renewalEveryDays, language);

  return (
    <div className="Ks-page">
      <Header language={language} onSelectLanguage={selectLanguage} />

      <div className="hero">
        <div className="hero-inner">
          <div className="hero-text">
            <span className="eyebrow">{t('Родительский контроль №1', '№1 ата-эне көзөмөл колдонмосу', 'Parental control app #1')}</span>
            <h1>{t('Спокойствие родителя. Безопасность ребёнка.', 'Ата-энеге тынчтык. Балага коопсуздук.', 'Peace of mind for parents. Safety for kids.')}</h1>
            <p className="hero-subtitle">
              {t(
                'Kid Security помогает отслеживать местоположение ребёнка, ограничивать экранное время и фильтровать контент — всё в одном приложении вместе с Mega.',
                'Kid Security балаңыздын жайгашкан жерин көзөмөлдөөгө, экран убактысын чектөөгө жана мазмунду чыпкалоого жардам берет — баары Mega менен бир колдонмодо.',
                'Kid Security helps you track your child’s location, limit screen time and filter content — all in one app, together with Mega.'
              )}
            </p>
            <button type="button" className="hero-cta" onClick={scrollToForm}>
              {t('Попробовать 3 дня бесплатно', '3 күн бекер сынап көрүү', 'Try 3 days for free')}
            </button>
          </div>
          <div className="hero-visual">
            <svg viewBox="0 0 240 260" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Kid Security">
              <circle cx="120" cy="140" r="112" fill="rgba(103, 193, 98, 0.14)" />
              <circle cx="52" cy="66" r="6" fill="#ffb020" opacity="0.55" />
              <circle cx="204" cy="158" r="5" fill="#67C162" opacity="0.55" />
              <path d="M120 14 C150 14 176 26 198 40 L198 122 C198 176 166 212 120 250 C74 212 42 176 42 122 L42 40 C64 26 90 14 120 14 Z" fill="#fff" stroke="#67C162" strokeWidth="4" />
              <circle cx="103" cy="118" r="22" fill="#1f8a3c" />
              <path d="M79 202 C79 158 85 138 103 138 C121 138 127 158 127 202 Z" fill="#1f8a3c" />
              <circle cx="147" cy="146" r="16" fill="#ffb020" />
              <path d="M131 202 C131 172 135 158 147 158 C159 158 163 172 163 202 Z" fill="#ffb020" />
              <circle cx="188" cy="56" r="28" fill="#fff" stroke="rgba(0,0,0,0.08)" />
              <path d="M188 40 C179 40 172 47 172 56 C172 68 188 84 188 84 C188 84 204 68 204 56 C204 47 197 40 188 40 Z" fill="#23c11d" />
              <circle cx="188" cy="55" r="6" fill="#fff" />
            </svg>
          </div>
        </div>
      </div>

      <div className="features">
        <h2>{t('Что получает родитель', 'Ата-эне эмне алат', 'What parents get')}</h2>
        <p className="section-subtitle">
          {t('Приложение №1 по родительскому контролю', 'Ата-эне көзөмөлү боюнча №1 колдонмо', 'The #1 app for parental control')}
        </p>
        <div className="features-grid">
          {FEATURES.map((feature) => (
            <div className="feature-card" key={feature.titleRu}>
              <div className="feature-icon">{feature.icon}</div>
              <h4>{t(feature.titleRu, feature.titleKy, feature.titleEn)}</h4>
              <p>{t(feature.descRu, feature.descKy, feature.descEn)}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="subscribe-section" ref={formSectionRef} id="subscribe">
        <div className="container">
          <div className="form-content">
            <div className="title">
              <h4 id="headup-title">{t('Скачайте приложение для родительского контроля Kid Security', 'Ата-эне көзөмөл колдонмосу Kid Security-ди жүктөп алыңыз', 'Download the Kid Security parental control app')}</h4>
            </div>
            <div className="form-content-middle">
              <div className="title">
                <h4 id="title">
                  {firstStep
                    ? t('Выберите тариф и введите номер телефона', 'Тарифти тандап, телефон номериңизди киргизиңиз', 'Choose a plan and enter your phone number')
                    : t('Введите код из SMS', 'SMS аркылуу келген кодду киргизиңиз', 'Enter the code from the SMS')}
                </h4>
              </div>

              {firstStep && (
                <div className="plan-select" role="radiogroup" aria-label={t('Выбор тарифа', 'Тарифти тандоо', 'Choose a plan')}>
                  {plans.map((plan) => (
                    <label
                      key={plan.planId}
                      className={`plan-option ${selectedPlanId === plan.planId ? 'selected' : ''}`}
                    >
                      {plan.planId === bestValuePlanId && (
                        <span className="plan-option-badge">{t('Выгодно', 'Пайдалуу', 'Best value')}</span>
                      )}
                      <input
                        type="radio"
                        name="plan"
                        checked={selectedPlanId === plan.planId}
                        onChange={() => setSelectedPlanId(plan.planId)}
                      />
                      <span className="plan-option-cycle">{describeCycleTitle(plan.renewalEveryDays, language)}</span>
                      <span className="plan-option-price">{formatPrice(plan.price)} {t('сом', 'сом', 'som')}</span>
                      {plan.freeDays > 0 && (
                        <span className="plan-option-trial">
                          {t(`${plan.freeDays} дня бесплатно`, `${plan.freeDays} күн акысыз`, `${plan.freeDays} days free`)}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              )}

              {firstStep && (
                <div className="phone-field">
                  <div className="phone-field-icon"><img alt="" src="/phone-icon.avif" /></div>
                  <div id="msisdn-input" className={`input ${warning ? 'vibrate' : ''}`}>
                    <span id="country-code" className="country-code" style={{ color: isMsisdnFocused || msisdn ? 'black' : '#999' }}>+996</span>
                    <span className="input-divider" />
                    <input id="msisdn-val" type="tel" value={msisdn} placeholder="55X XXX XXX" autoComplete="off" minLength={9} maxLength={9} required onChange={handleMsisdnChange} onFocus={handleMsisdnFocus} onBlur={handleMsisdnBlur} />
                  </div>
                </div>
              )}

              {!firstStep && (
                <div className={`input otp-input ${warning ? 'vibrate' : ''}`}>
                  <input type="tel" value={otp} placeholder="X X X X" autoComplete="off" minLength={4} maxLength={4} required onChange={handleOtpChange} ref={otpBoxRef} />
                </div>
              )}

              <div className={`warning-msg ${warning ? 'active' : ''}`}>
                <span>{warning || ' '}</span>
              </div>

              <div className="button">
                {firstStep ? (
                  <button id="send-otp-btn" onClick={handleSendOtpClick} disabled={loading}>
                    {loading
                      ? t('Отправка...', 'Жиберилүүдө...', 'Sending...')
                      : t('Подписаться', 'Жазылуу', 'Subscribe')}
                  </button>
                ) : (
                  <button id="verify-otp-btn" onClick={handleVerifyOtpClick} disabled={loading}>
                    {loading
                      ? t('Проверка...', 'Текшерилүүдө...', 'Checking...')
                      : t('Подтвердить', 'Ырастоо', 'Confirm')}
                  </button>
                )}
              </div>
            </div>

            <div className="form-content-bottom">
              {language === 'RU' ? (
                <span>
                  Kid Security — сервис родительского контроля: фильтрация контента, геолокация ребёнка и контроль экранного времени.
                  {selectedPlan.freeDays > 0 && ` Первые ${selectedPlan.freeDays} дня — бесплатно, далее`} подписка стоимостью {selectedPrice} сом {selectedCycleSuffix}, продление автоматическое.
                  Вы можете отменить подписку в любой момент. Услуга доступна абонентам Mega.
                  Оформляя подписку, вы подтверждаете, что вам исполнилось 18 лет или получено согласие родителя либо законного представителя.
                </span>
              ) : language === 'KY' ? (
                <span>
                  Kid Security — балдарды санарип дүйнөдө коргоочу ата-эне көзөмөл кызматы: мазмунду чыпкалоо, баланын жайгашкан жерин билүү жана экран убактысын көзөмөлдөө.
                  {selectedPlan.freeDays > 0 && ` Алгачкы ${selectedPlan.freeDays} күн — акысыз, андан кийин`} {selectedPrice} сом баада {selectedCycleSuffix} жазылуу автоматтык түрдө узартылат.
                  Жазылууну каалаган убакта жокко чыгара аласыз. Кызмат Mega абоненттерине жеткиликтүү.
                  Жазылуу менен сиз 18 жашка толгонуңузду же ата-эне/мыйзамдуу өкүлдүн уруксатын алганыңызды тастыктайсыз.
                </span>
              ) : (
                <span>
                  Kid Security is a parental control service: content filtering, child location tracking and screen time control.
                  {selectedPlan.freeDays > 0 && ` The first ${selectedPlan.freeDays} days are free, then`} the subscription costs {selectedPrice} som {selectedCycleSuffix}, renewing automatically.
                  You can cancel the subscription at any time. The service is available to Mega subscribers.
                  By subscribing, you confirm you are 18 or older, or that parental/legal guardian consent has been given.
                </span>
              )}
            </div>

            <div className="form-content-footer">
              <Link to={`/unsub?lang=${language}`}>{t('Отменить подписку', 'Жазылуудан баш тартуу', 'Cancel subscription')}</Link>
            </div>
          </div>
        </div>
      </div>

      <div className="steps">
        <h2>{t('Как начать пользоваться Kid Security', 'Kid Security кантип колдонуп баштасак болот', 'How to start using Kid Security')}</h2>
        <div className="steps-grid">
          <div className="step-card">
            <span className="step-number">1</span>
            <h4>{t('Оформите подписку', 'Жазылууну ырастаңыз', 'Subscribe')}</h4>
            <p>{t('Выберите тариф выше, введите номер телефона и подтвердите код из SMS.', 'Жогорудагы тарифти тандап, телефон номериңизди киргизиңиз жана SMS кодду ырастаңыз.', 'Pick a plan above, enter your phone number and confirm the SMS code.')}</p>
          </div>
          <div className="step-card">
            <span className="step-number">2</span>
            <h4>{t('Скачайте приложение', 'Колдонмону жүктөп алыңыз', 'Download the app')}</h4>
            <p>{t('Установите Kid Security на свой телефон — ссылка придёт вам в SMS.', 'Kid Security колдонмосун телефонуңузга орнотуңуз — шилтеме SMS менен келет.', 'Install Kid Security on your phone — you’ll get the link by SMS.')}</p>
          </div>
          <div className="step-card">
            <span className="step-number">3</span>
            <h4>{t('Настройте профиль ребёнка', 'Баланын профилин жөндөңүз', 'Set up your child’s profile')}</h4>
            <p>{t('Свяжите телефон ребёнка с приложением и включите нужные функции контроля.', 'Баланын телефонун колдонмого туташтырып, керектүү көзөмөл функцияларын иштетиңиз.', 'Link your child’s phone to the app and turn on the controls you need.')}</p>
          </div>
        </div>
      </div>

      <SiteFooter t={t} language={language} />
    </div>
  );
}

export default PinPage;
